import { createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, FullscreenIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { useQuickSightEmbedUrl } from '@/features/quicksight/hooks/useQuickSightEmbedUrl';
import { AppPageContent } from '@/features/shared/components/AppPageContent';
import { AppPageHeader } from '@/features/shared/components/AppPageHeader';
import { UserRoles } from '@/lib/constants/auth.constants.ts';
import { resolveDashboardId } from '@/lib/quicksight';
import { requireRole } from '@/lib/validation';

export const Route = createFileRoute('/_app/app/daily-metrics')({
  component: RouteComponent,
  ssr: false,
  beforeLoad: requireRole([UserRoles.ADMINISTRATOR]),
});

function RouteComponent() {
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const dashboardRef = useRef<HTMLDivElement>(null);

  const dashboardId = resolveDashboardId('dailyMetrics');
  const {
    data,
    isLoading: isEmbedLoading,
    isError: isEmbedError,
  } = useQuickSightEmbedUrl(dashboardId);

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement !== null);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!isFullscreen && dashboardRef.current) {
      if (dashboardRef.current.requestFullscreen) {
        dashboardRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  return (
    <AppPageContent>
      <AppPageHeader
        title="Daily Metrics"
        breadcrumbItems={[
          {
            label: 'App',
            link: { to: '/app' },
          },
          {
            label: 'Daily Metrics',
            link: { to: '/app/daily-metrics' },
          },
        ]}
      />
      <div className="p-4 w-full max-w-full">
        {isEmbedError && (
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Failed to load the dashboard. Please try refreshing the page.
            </AlertDescription>
          </Alert>
        )}
        <div
          ref={dashboardRef}
          className="dashboard-container relative"
          style={{
            width: '100%',
            maxWidth: '100%',
            height: 'calc(100vh - 150px)',
            minHeight: '600px',
            overflow: 'hidden',
          }}
        >
          <button
            onClick={toggleFullscreen}
            className="absolute top-2 right-2 z-20 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center opacity-80 hover:opacity-100"
          >
            <FullscreenIcon className="h-5 w-5 mr-1" />

            {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          </button>

          {(isEmbedLoading || isLoading) && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75 z-10">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-2 text-gray-700">Loading dashboard...</p>
              </div>
            </div>
          )}

          {data?.embedUrl && (
            <iframe
              src={data.embedUrl}
              style={{
                border: 'none',
                width: '100%',
                height: '100%',
                overflow: 'auto',
              }}
              title="AWS QuickSight Dashboard"
              onLoad={() => setIsLoading(false)}
            />
          )}
        </div>
      </div>
    </AppPageContent>
  );
}
